import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Loader2, Zap, ArrowRight, CheckCircle2 } from "lucide-react";

export default function Signup() {
  const { user, fetchMe } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", company_name: "" });
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/app" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Senha deve ter no mínimo 8 caracteres");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/signup", form);
      toast.success("Conta criada com sucesso! 7 dias grátis iniciados.");
      if (fetchMe) await fetchMe();
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao criar conta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded bg-emerald-500 flex items-center justify-center">
            <Zap className="w-5 h-5 text-slate-950" />
          </div>
          <span className="text-xl font-black tracking-tight">Vyntric</span>
        </Link>

        <div className="mb-6">
          <h1 className="text-3xl font-black tracking-tighter">Crie sua conta</h1>
          <p className="text-sm text-slate-400 mt-1">
            <span className="text-emerald-400 font-bold">7 dias grátis</span> · Sem cartão · Cancele quando quiser
          </p>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="signup-form">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">Nome completo</span>
            <input
              data-testid="signup-name"
              type="text"
              value={form.name}
              onChange={set("name")}
              required
              className="w-full bg-white/5 border border-white/10 focus:border-emerald-500 px-3 py-2.5 text-sm rounded-sm outline-none"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">E-mail</span>
            <input
              data-testid="signup-email"
              type="email"
              value={form.email}
              onChange={set("email")}
              required
              className="w-full bg-white/5 border border-white/10 focus:border-emerald-500 px-3 py-2.5 text-sm rounded-sm outline-none"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">Nome da empresa</span>
            <input
              data-testid="signup-company"
              type="text"
              value={form.company_name}
              onChange={set("company_name")}
              required
              className="w-full bg-white/5 border border-white/10 focus:border-emerald-500 px-3 py-2.5 text-sm rounded-sm outline-none"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">Senha (mín. 8 caracteres)</span>
            <input
              data-testid="signup-password"
              type="password"
              value={form.password}
              onChange={set("password")}
              required
              minLength={8}
              className="w-full bg-white/5 border border-white/10 focus:border-emerald-500 px-3 py-2.5 text-sm rounded-sm outline-none"
            />
          </label>

          <div className="pt-2 space-y-1.5">
            {[
              "Faturamento automático 24/7",
              "1 Bling ERP incluído",
              "Sem cobrança nos primeiros 7 dias",
            ].map((f) => (
              <div key={f} className="flex items-center gap-2 text-xs text-slate-300">
                <CheckCircle2 className="w-3 h-3 text-emerald-400" /> {f}
              </div>
            ))}
          </div>

          <button
            type="submit"
            data-testid="signup-submit"
            disabled={loading}
            className="w-full px-5 py-3 text-sm font-black uppercase tracking-wider bg-emerald-500 text-slate-950 hover:bg-emerald-400 rounded-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            Criar minha conta grátis
          </button>

          <div className="text-center text-xs text-slate-500 pt-2">
            Já tem conta? <Link to="/login" data-testid="signup-login-link" className="text-emerald-400 font-bold hover:underline">Entrar</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
